import { apiClient } from '@/lib/api/client';
import type {
  Payout,
  PayoutDestination,
  SellerOnboardingStatus,
  StartOnboardingResult,
  CreatePayoutMethodData,
  RequestPayoutData,
  PayoutFilters,
  PaginatedResponse,
  EligiblePspsResult,
} from '../types/payout.types';

interface ApiData<T> {
  data: T;
}

export const payoutService = {
  /**
   * Get seller onboarding status and payout methods
   */
  async getOnboardingStatus(): Promise<SellerOnboardingStatus> {
    const response = await apiClient.get<ApiData<SellerOnboardingStatus>>(
      '/payment/seller/onboarding/status'
    );
    return response.data?.data ?? {
      isOnboarded: false,
      status: 'NOT_STARTED',
      payoutMethods: [],
    };
  },

  /**
   * Start or continue onboarding process.
   * - mode HOSTED               → `onboardingUrl` (redirection Stripe Connect).
   * - mode DESTINATION_REQUIRED → le vendeur doit ajouter une destination de
   *   payout (IBAN / mobile money) — corridors non-Stripe (Comores…).
   */
  async startOnboarding(returnUrl?: string): Promise<StartOnboardingResult> {
    const response = await apiClient.post<ApiData<StartOnboardingResult>>(
      '/payment/seller/onboarding/start',
      { returnUrl: returnUrl || window.location.href }
    );
    return response.data?.data ?? { mode: 'HOSTED', onboardingUrl: '' };
  },

  /**
   * Doc 51 Phase 5 — PSP éligibles à proposer au vendeur (Popup 1). Le serveur
   * retourne les connecteurs Connect actifs couvrant le pays + la destination
   * manuelle (IBAN / mobile money).
   */
  async getEligiblePsps(): Promise<EligiblePspsResult> {
    const response = await apiClient.get<ApiData<EligiblePspsResult>>(
      '/payment/seller/eligible-psps'
    );
    return response.data?.data ?? { options: [] };
  },

  /**
   * Doc 51 Phase 5 — MFA vendeur (email OTP). Le vendeur demande un code avant
   * toute création/modification de destination (la Phase 4 rend le MFA
   * obligatoire côté serveur : sans challenge vérifié, l'ajout renvoie 403).
   */
  async requestMfaChallenge(
    action = 'PAYOUT_DESTINATION'
  ): Promise<{ challengeId: string; expiresAt: string }> {
    const response = await apiClient.post<ApiData<{ challengeId: string; expiresAt: string }>>(
      '/payment/seller/mfa/challenge',
      { action }
    );
    return response.data.data;
  },

  /** Vérifie le code OTP reçu par email (avant l'action payout). */
  async verifyMfaChallenge(challengeId: string, code: string): Promise<void> {
    await apiClient.post('/payment/seller/mfa/verify', { challengeId, code });
  },

  /**
   * Get all payout methods for the seller — le serveur renvoie des
   * `PayoutDestination[]` bruts du SDK (`controller.ts:1186`), pas de remap.
   */
  async getPayoutMethods(): Promise<PayoutDestination[]> {
    const response = await apiClient.get<ApiData<PayoutDestination[]>>(
      '/payment/seller/payout-methods'
    );
    return response.data?.data ?? [];
  },

  /**
   * Add a new payout method. `data` reste `CreatePayoutMethodData` (le corps
   * ÉCRITURE, remappé côté serveur) — seule la réponse (la destination créée)
   * est une `PayoutDestination` brute du SDK.
   */
  async addPayoutMethod(data: CreatePayoutMethodData): Promise<PayoutDestination> {
    const response = await apiClient.post<ApiData<PayoutDestination>>(
      '/payment/seller/payout-methods',
      data
    );
    return response.data.data;
  },

  /**
   * Update a payout method
   */
  async updatePayoutMethod(id: string, data: Partial<CreatePayoutMethodData>): Promise<PayoutDestination> {
    const response = await apiClient.patch<ApiData<PayoutDestination>>(
      `/payment/seller/payout-methods/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a payout method
   */
  async deletePayoutMethod(id: string): Promise<void> {
    await apiClient.delete(`/payment/seller/payout-methods/${id}`);
  },

  /**
   * Set a payout method as default
   */
  async setDefaultPayoutMethod(id: string): Promise<PayoutDestination> {
    const response = await apiClient.post<ApiData<PayoutDestination>>(
      `/payment/seller/payout-methods/${id}/default`
    );
    return response.data.data;
  },

  /**
   * Get list of payouts
   */
  async getPayouts(filters?: PayoutFilters): Promise<PaginatedResponse<Payout>> {
    const params = new URLSearchParams();
    if (filters?.cursor) params.set('cursor', filters.cursor);
    params.set('limit', String(filters?.limit || 20));
    if (filters?.status) params.set('status', filters.status);

    const response = await apiClient.get<ApiData<PaginatedResponse<Payout>>>(
      `/payment/seller/payouts?${params.toString()}`
    );
    return response.data?.data ?? { data: [], hasMore: false, nextCursor: null };
  },

  /**
   * Get a single payout by ID
   */
  async getPayout(id: string): Promise<Payout | null> {
    const response = await apiClient.get<ApiData<Payout>>(
      `/payment/seller/payouts/${id}`
    );
    return response.data?.data ?? null;
  },

  /**
   * Request a new payout
   */
  async requestPayout(data: RequestPayoutData): Promise<Payout> {
    const response = await apiClient.post<ApiData<Payout>>(
      '/payment/seller/payouts',
      data
    );
    return response.data.data;
  },

  /**
   * Cancel a pending payout
   */
  async cancelPayout(id: string): Promise<Payout> {
    const response = await apiClient.post<ApiData<Payout>>(
      `/payment/seller/payouts/${id}/cancel`
    );
    return response.data.data;
  },
};
